import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../store/store";
import httpService from "@shared/services/http.service";

// Types matching backend specs
export interface CallRequest {
    id: string;
    sender_id: string;
    sender_name?: string;
    sender_avatar?: string;
    sender_role: "customer" | "curator" | "admin";
    receiver_id: string;
    call_type: "audio" | "video";
    status: "ringing" | "connected" | "failed" | "ended";
    timestamp: string;
}

export interface ActiveCall {
    id: string;
    room_name: string;
    token: string;
    ws_url: string;
    participants: CallParticipant[];
    started_at: string;
    call_type: "audio" | "video";

    // UI Fields
    callRequestId?: string;
    name: string;
    initials: string;
    status: "Active" | "Pending" | "Scheduled" | "Completed";
    curator: string;
    location: string;
    email: string;
    phone: string;
    memberSince: string;
    purpose: string;
    interests: string[];
    rating: number;
    time: string;
    avatarColor: string;
}

export interface CallParticipant {
    id: string;
    identity: string;
    name?: string;
    avatar?: string;
    role: string;
    isAudioEnabled: boolean;
    isVideoEnabled: boolean;
    isScreenSharing: boolean;
}

export interface AdminStats {
    active_calls: number;
    pending_requests: number;
    online_users: number;
    active_calls_list: {
        id: string;
        room_name: string;
        participants: { name: string; role: string; avatar?: string }[];
        start_time: string;
        initiator_name: string;
        call_type: "audio" | "video";
    }[];
}

export interface CuratorCallHistoryItem {
    id: string;
    customer_name: string;
    customer_avatar?: string;
    last_call_time: string;
    status: 'ringing' | 'active' | 'missed' | 'completed' | 'rejected';
    duration?: number;
    call_type?: 'audio' | 'video';
}

// Admin-specific types for QA panel parity
export interface RoutingState {
    callRequestId: string;
    curatorId: string;
    curatorProfile?: any;
    hopCount: number;
    status: 'ringing' | 'timeout';
    strategy: string;
}

export interface OnlineUser {
    id: string;
    name: string;
    role: string;
    status: 'online' | 'offline';
}

interface CallState {
    activeCall: ActiveCall | null;
    incomingCall: CallRequest | null;
    isWindowOpen: boolean;
    isMinimized: boolean;
    adminStats: AdminStats | null;
    curatorHistory: CuratorCallHistoryItem[];
    isLoadingHistory: boolean;
    connectionStatus: "connected" | "disconnected" | "reconnecting" | "idle";
    error: string | null;
    // Admin panel state for QA parity
    routingStates: Record<string, RoutingState>;
    onlineUsers: OnlineUser[];
}

const initialState: CallState = {
    activeCall: null,
    incomingCall: null,
    isWindowOpen: false,
    isMinimized: false,
    adminStats: null,
    curatorHistory: [],
    isLoadingHistory: false,
    connectionStatus: "idle",
    error: null,
    // Admin panel state
    routingStates: {},
    onlineUsers: [],
};

// Params for curator history API
export interface CuratorHistoryParams {
    curatorId: string;
    page?: number;
    limit?: number;
    status?: string;
    call_type?: 'audio' | 'video';
    from_date?: string;
    to_date?: string;
    sort_by?: 'request_timestamp' | 'duration_seconds';
    sort_order?: 'asc' | 'desc';
}

// Async Thunk for fetching curator history
export const fetchCuratorCallHistory = createAsyncThunk(
    "call/fetchCuratorHistory",
    async (params: CuratorHistoryParams, { rejectWithValue }) => {
        try {
            const { curatorId, page = 1, limit = 20, ...filters } = params;

            // Build query string
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, v]) => v !== undefined)
                )
            });

            const response = await httpService.get(
                `video-call/curator/${curatorId}/history?${queryParams.toString()}`
            );

            // Map API response to frontend format
            const items: CuratorCallHistoryItem[] = response.data.map((item: any) => ({
                id: item.id,
                customer_name: item.customer_name,
                customer_avatar: item.customer_avatar,
                last_call_time: item.request_timestamp,
                status: mapApiStatusToHistoryStatus(item.status),
                duration: item.duration_seconds,
                call_type: item.call_type
            }));

            return items;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch history");
        }
    }
);

// Helper to map API status to our history status type
const mapApiStatusToHistoryStatus = (apiStatus: string): 'ringing' | 'active' | 'missed' | 'completed' | 'rejected' => {
    switch (apiStatus) {
        case 'ended':
            return 'completed';
        case 'connected':
        case 'accepted':
            return 'active';
        case 'ringing':
        case 'pending':
            return 'ringing';
        case 'rejected':
        case 'cancelled':
            return 'rejected';
        case 'missed':
        case 'timeout':
        case 'failed':
            return 'missed';
        default:
            return 'missed';
    }
};

export const callSlice = createSlice({
    name: "call",
    initialState,
    reducers: {
        setIncomingCall: (state, action: PayloadAction<CallRequest | null>) => {
            state.incomingCall = action.payload;
        },
        setActiveCall: (state, action: PayloadAction<ActiveCall | null>) => {
            state.activeCall = action.payload;
            if (action.payload) {
                state.connectionStatus = "connected";
                state.isWindowOpen = true;
                state.isMinimized = false;
            } else {
                state.connectionStatus = "idle";
                state.isWindowOpen = false;
            }
        },
        updateConnectionStatus: (state, action: PayloadAction<"connected" | "disconnected" | "reconnecting" | "idle">) => {
            state.connectionStatus = action.payload;
        },
        // setCallError is now handled by extraReducers for async thunks, but kept here if direct setting is needed.
        setCallError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setAdminStats: (state, action: PayloadAction<AdminStats>) => {
            state.adminStats = action.payload;
        },
        setWindowOpen: (state, action: PayloadAction<boolean>) => {
            state.isWindowOpen = action.payload;
            if (!action.payload) {
                state.isMinimized = false;
            }
        },
        setMinimized: (state, action: PayloadAction<boolean>) => {
            state.isMinimized = action.payload;
        },
        endCallCleanup: (state) => {
            state.activeCall = null;
            state.connectionStatus = "idle";
            state.isWindowOpen = false;
            state.incomingCall = null;
            state.error = null; // Clear any errors
        },
        addHistoryItem: (state, action: PayloadAction<CuratorCallHistoryItem>) => {
            // Add to the beginning of the list, avoid duplicates
            if (!state.curatorHistory.find(item => item.id === action.payload.id)) {
                state.curatorHistory.unshift(action.payload);
            }
        },
        updateHistoryItemStatus: (state, action: PayloadAction<{ id: string, status: CuratorCallHistoryItem['status'], duration?: number }>) => {
            const item = state.curatorHistory.find(i => i.id === action.payload.id);
            if (item) {
                item.status = action.payload.status;
                if (action.payload.duration) {
                    item.duration = action.payload.duration;
                }
            }
        },
        // Admin Incremental Updates
        adminCallCreated: (state, action: PayloadAction<any>) => {
            if (state.adminStats) {
                state.adminStats.pending_requests += 1;
            }
        },
        adminCallAccepted: (state, action: PayloadAction<any>) => {
            if (state.adminStats) {
                // Decrement pending, increment active
                state.adminStats.pending_requests = Math.max(0, state.adminStats.pending_requests - 1);
                state.adminStats.active_calls += 1;

                // Add to active_calls_list if not exists (avoid duplicates)
                if (!state.adminStats.active_calls_list.find(c => c.id === action.payload.activeCallId)) {
                    state.adminStats.active_calls_list.unshift({
                        id: action.payload.activeCallId,
                        room_name: action.payload.room_name || "Room",
                        participants: [],
                        start_time: action.payload.start_time || new Date().toISOString(),
                        initiator_name: action.payload.initiator_name || "User",
                        call_type: action.payload.call_type || "video"
                    });
                }
            }
        },
        adminCallRejected: (state, action: PayloadAction<any>) => {
            if (state.adminStats) {
                state.adminStats.pending_requests = Math.max(0, state.adminStats.pending_requests - 1);
            }
        },
        adminCallMissed: (state, action: PayloadAction<any>) => {
            if (state.adminStats) {
                state.adminStats.pending_requests = Math.max(0, state.adminStats.pending_requests - 1);
            }
        },
        adminCallEnded: (state, action: PayloadAction<any>) => {
            if (state.adminStats) {
                state.adminStats.active_calls = Math.max(0, state.adminStats.active_calls - 1);
                state.adminStats.active_calls_list = state.adminStats.active_calls_list.filter(c => c.id !== action.payload.activeCallId);
            }
        },
        adminCallCancelled: (state, action: PayloadAction<any>) => {
            if (state.adminStats) {
                state.adminStats.pending_requests = Math.max(0, state.adminStats.pending_requests - 1);
            }
            // Clean up routing state for this request
            const requestId = action.payload.callRequestId;
            if (requestId && state.routingStates[requestId]) {
                delete state.routingStates[requestId];
            }
        },
        updateRoutingState: (state, action: PayloadAction<RoutingState>) => {
            state.routingStates[action.payload.callRequestId] = action.payload;
        },
        updateOnlineUsers: (state, action: PayloadAction<{ userId: string; status: 'online' | 'offline'; role?: string; name?: string }>) => {
            const { userId, status, role, name } = action.payload;
            if (status === 'online') {
                // Add user if not exists
                if (!state.onlineUsers.find(u => u.id === userId)) {
                    state.onlineUsers.push({
                        id: userId,
                        name: name || 'User',
                        role: role || 'user',
                        status: 'online'
                    });
                }
            } else {
                // Remove user
                state.onlineUsers = state.onlineUsers.filter(u => u.id !== userId);
            }
        },
        clearRoutingState: (state, action: PayloadAction<string>) => {
            delete state.routingStates[action.payload];
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCuratorCallHistory.pending, (state) => {
                state.isLoadingHistory = true;
                state.error = null; // Clear any previous errors
            })
            .addCase(fetchCuratorCallHistory.fulfilled, (state, action) => {
                state.isLoadingHistory = false;
                state.curatorHistory = action.payload;
            })
            .addCase(fetchCuratorCallHistory.rejected, (state, action) => {
                state.isLoadingHistory = false;
                state.error = action.payload as string;
            });
    }
});

export const {
    setIncomingCall,
    setActiveCall,
    updateConnectionStatus,
    setAdminStats,
    setWindowOpen,
    setMinimized,
    setCallError,
    endCallCleanup,
    addHistoryItem,
    updateHistoryItemStatus,
    adminCallCreated,
    adminCallAccepted,
    adminCallRejected,
    adminCallMissed,
    adminCallEnded,
    adminCallCancelled,
    updateRoutingState,
    updateOnlineUsers,
    clearRoutingState
} = callSlice.actions;

export const selectActiveCall = (state: RootState) => state.call.activeCall;
export const selectIncomingCall = (state: RootState) => state.call.incomingCall;
export const selectIsWindowOpen = (state: RootState) => state.call.isWindowOpen;
export const selectIsMinimized = (state: RootState) => state.call.isMinimized;
export const selectAdminStats = (state: RootState) => state.call.adminStats;
export const selectCuratorHistory = (state: RootState) => state.call.curatorHistory;
export const selectIsLoadingHistory = (state: RootState) => state.call.isLoadingHistory;
export const selectCallStatus = (state: RootState) => state.call.connectionStatus;
export const selectCallError = (state: RootState) => state.call.error;
export const selectRoutingStates = (state: RootState) => state.call.routingStates;
export const selectOnlineUsers = (state: RootState) => state.call.onlineUsers;

export default callSlice.reducer;

